// @flow
import * as React from 'react';
import styled from 'styled-components';
import invariant from 'invariant';
import type { FileUploadTypes } from 'api/shared';
import Box from 'components/Box';
import { isNotEmpty, partial, entries } from 'utils/functional';

import { getJson, postData, postJson } from 'utils/http';
import { waitTaskCompleted } from 'utils/asyncBackendTask';
import { configValue } from 'utils/config';
import { fileIdToUrl } from 'utils/s3file';

const { useEffect, useState, useCallback, useRef } = React;

const InputContainer = styled(Box)`
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    &[disabled] {
      cursor: not-allowed;
    }
  }
`;

const objToFormData = (o: Object): FormData => {
  const f = new FormData();
  entries(o).forEach(([k, v]) => f.append(k, v));
  return f;
};

const uploadPresignedUrl = (f: File, r: Object) => {
  const { params, s3File } = r;
  const formData = objToFormData(params.fields);
  formData.append('file', f);
  return postData(params.url, formData).then(() =>
    postJson(`/data/s3-uploads/${s3File.id}/done`).then(({ task }) =>
      waitTaskCompleted(task.url).then(() => s3File.id)
    )
  );
};

const useMock = configValue('idealist', 'fileUploadMock');

const uploadToS3 = (f: File, fileType: FileUploadTypes) =>
  postJson('/data/s3-uploads', {
    fileType,
    filename: f.name,
    size: f.size, // bytes
    mimeType: f.type
  }).then(partial(uploadPresignedUrl, f));

const S3Picker = ({
  children,
  name,
  downloadAttachment,
  isRequired,
  qaId,
  fileId,
  fileType,
  onChange,
  onError,
  accept,
  'aria-label': ariaLabel,
  disabled
}: {
  children: Object => React.Node,
  downloadAttachment?: boolean,
  name: string,
  isRequired: boolean,
  qaId: string,
  fileId: ?string,
  fileType: FileUploadTypes,
  onChange: (?string) => void,
  onError?: Error => void,
  accept?: string,
  'aria-label'?: string,
  disabled?: boolean
}) => {
  const inputRef = useRef<?HTMLInputElement>(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [filename, setFilename] = useState(null);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [mouseActive, setMouseActive] = useState(false);

  const onMouseEnter = () => {
    setMouseActive(true);
  };

  const onMouseLeave = () => {
    setMouseActive(false);
  };

  const onDragEnter = () => {
    setDragActive(true);
    setMouseActive(true);
  };

  const onDragEnd = () => {
    setDragActive(false);
    setMouseActive(false);
  };

  const onClick = () => {
    if (!useMock && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onFileChange = useCallback(
    () => {
      const fs = inputRef.current && inputRef.current.files;
      if (isNotEmpty(fs)) {
        invariant(fs, 'fs must be defined'); // for flow
        const f = fs[0];
        setLoading(true);
        uploadToS3(f, fileType)
          .then(newFileId => {
            setFilename(f.name);
            setCreated(new Date());
            setUrlPreview(URL.createObjectURL(f));
            onChange(newFileId);
          })
          .catch(e => {
            if (onError) onError(e);
          })
          .finally(() => {
            setLoading(false);
            setDragActive(false);
            setMouseActive(false);
          });
      }
    },
    [fileType, onChange, onError]
  );

  useEffect(
    () => {
      if (fileId && !filename) {
        setLoading(true);
        getJson(`/data/s3-uploads/${fileId}/meta`)
          .then(({ s3File }) => {
            setFilename(s3File.filename);
            setCreated(s3File.created);
          })
          .finally(() => {
            setLoading(false);
          });
      }
      if (fileId && !urlPreview) {
        setUrlPreview(fileIdToUrl(fileId, downloadAttachment));
      }
    },
    [fileId, urlPreview, filename, downloadAttachment]
  );

  return (
    <InputContainer data-qa-id={qaId}>
      <input
        aria-label={ariaLabel}
        ref={inputRef}
        required={isRequired}
        name={name}
        type="file"
        onChange={onFileChange}
        disabled={disabled}
        data-qa-id={`${qaId}-hidden-input`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragEnter={onDragEnter}
        onDragEnd={onDragEnd}
        onDragLeave={onDragEnd}
        onFocus={onMouseEnter}
        onBlur={onMouseLeave}
        accept={accept}
      />
      {children({
        onClick,
        urlPreview,
        filename,
        loading,
        dragActive: !disabled && dragActive,
        mouseActive: !disabled && mouseActive,
        created
      })}
    </InputContainer>
  );
};

export default S3Picker;
